/**
 * The conversation: a live list of segments, each with a speaker label and
 * colour, and a distinct marker where the label is still a guess.
 *
 * A speaker's name is shown once per run of their lines, and a line the
 * end-of-meeting correction pass rewrites is highlighted briefly and marked,
 * so a change reads as the transcript settling rather than as text jumping.
 *
 * Owns the scrollable column, reports its segment geometry upward (so the
 * scroll-anchor maths has real measurements to work with), and carries the
 * touch overlay.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Segment } from '@lt/protocol';
import type { SegmentBox } from '../lib/scroll-anchor.js';
import { isLowConfidence, speakerColorIndex, type TranscriptState } from '../lib/transcript-state.js';
import { speakerName } from '../lib/transcript-state.js';
import { clock, continuations, hiddenLabels, wasCorrected } from '../lib/transcript-view.js';

/** How long a corrected line stays highlighted. */
const CORRECTION_FLASH_MS = 2_500;

export interface ConversationViewProps {
  state: TranscriptState;
  /** Controller only: tapping a label renames that speaker for everyone. */
  onRenameSpeaker?: (speakerKey: string, currentName: string) => void;
  /** Called after every layout change, with boxes in content coordinates. */
  onGeometry?: (boxes: SegmentBox[], element: HTMLDivElement) => void;
  onScroll?: (element: HTMLDivElement) => void;
  columnRef?: (element: HTMLDivElement | null) => void;
  children?: React.ReactNode;
}

export function ConversationView(props: ConversationViewProps): JSX.Element {
  const { state, onRenameSpeaker, onGeometry, onScroll, columnRef, children } = props;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const nodesRef = useRef(new Map<string, HTMLElement>());

  const measure = useCallback(() => {
    const element = scrollRef.current;
    if (!element || !onGeometry) return;
    const boxes: SegmentBox[] = [];
    for (const segment of state.segments) {
      const node = nodesRef.current.get(segment.id);
      if (!node) continue;
      // offsetTop and offsetLeft are relative to the scroll container, which
      // is exactly the content coordinate space the anchor maths expects.
      boxes.push({
        id: segment.id,
        top: node.offsetTop,
        height: node.offsetHeight,
        left: node.offsetLeft,
        width: node.offsetWidth,
      });
    }
    onGeometry(boxes, element);
  }, [onGeometry, state.segments]);

  // Measure after the browser has laid out the new segments, not before.
  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    // A rotated phone or a resized window changes every box, so re-measure
    // rather than trusting the last layout.
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  const continued = useMemo(() => continuations(state.segments), [state.segments]);
  const hidden = useMemo(() => hiddenLabels(state.segments), [state.segments]);

  const setNode = useCallback((id: string, node: HTMLElement | null) => {
    if (node) nodesRef.current.set(id, node);
    else nodesRef.current.delete(id);
  }, []);

  return (
    <div
      className="conversation"
      ref={(element) => {
        scrollRef.current = element;
        columnRef?.(element);
      }}
      onScroll={(event) => onScroll?.(event.currentTarget)}
    >
      {state.loading && state.segments.length === 0 ? (
        <p className="placeholder">Catching up…</p>
      ) : null}
      {!state.loading && !state.error && state.segments.length === 0 ? (
        <p className="placeholder">
          {state.workerStatus === 'ready'
            ? 'Listening. The first line will appear here.'
            : 'Waiting for the transcription worker.'}
        </p>
      ) : null}

      {/* Not positioned: each line's offsetTop must stay relative to the scroll container. */}
      <ol className="segments">
        {state.segments.map((segment, index) => (
          <SegmentRow
            key={segment.id}
            segment={segment}
            name={speakerName(state, segment.speakerKey)}
            runStart={!(continued[index] ?? false)}
            hideLabel={hidden[index] ?? false}
            onRenameSpeaker={onRenameSpeaker}
            setNode={setNode}
          />
        ))}
      </ol>
      {children}
    </div>
  );
}

/**
 * Whether the correction pass has rewritten this line, and whether that
 * happened recently enough to still be highlighted.
 */
function useCorrection(segment: Segment): { corrected: boolean; flashing: boolean } {
  const previous = useRef<Segment | undefined>(undefined);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [corrected, setCorrected] = useState(false);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    const changed = wasCorrected(previous.current, segment);
    previous.current = segment;
    if (!changed) return;
    setCorrected(true);
    setFlashing(true);
    // Not an effect cleanup: a later update to the same line must not cancel
    // the timer and leave the highlight on for good.
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFlashing(false), CORRECTION_FLASH_MS);
  }, [segment]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return { corrected, flashing };
}

function SegmentRow(props: {
  segment: Segment;
  name: string;
  /** First line of a speaker's run, so it carries the time. */
  runStart: boolean;
  /** Same speaker as the line above, so the name is not repeated. */
  hideLabel: boolean;
  onRenameSpeaker?: (speakerKey: string, currentName: string) => void;
  setNode: (id: string, node: HTMLElement | null) => void;
}): JSX.Element {
  const { segment, name, runStart, hideLabel, onRenameSpeaker, setNode } = props;
  const lowConfidence = isLowConfidence(segment);
  const { corrected, flashing } = useCorrection(segment);

  return (
    <li
      ref={(node) => setNode(segment.id, node)}
      className={[
        'segment',
        `speaker-${speakerColorIndex(segment.speakerKey)}`,
        segment.isFinal ? 'final' : 'partial',
        lowConfidence ? 'uncertain' : '',
        hideLabel ? 'continued' : '',
        corrected ? 'corrected' : '',
        flashing ? 'flash' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-segment-id={segment.id}
      data-speaker={segment.speakerKey}
      data-revision={segment.revision}
    >
      {/* The run's time sits in the margin, the way a minute-taker writes it. */}
      {runStart ? <time className="at">{clock(segment.startMicros)}</time> : null}
      <div className="who">
        <button
          type="button"
          className="speaker"
          // A viewer has no rename affordance, so the label is not a button there.
          disabled={!onRenameSpeaker}
          onClick={() => onRenameSpeaker?.(segment.speakerKey, name)}
          title={
            lowConfidence
              ? `${name} — the label is still a draft (${Math.round(segment.confidence * 100)}% confident)`
              : onRenameSpeaker
                ? `Rename ${name}`
                : name
          }
        >
          <span className="name">{name}</span>
          {lowConfidence ? (
            <span className="uncertain-marker" aria-label="uncertain speaker">
              {Math.round(segment.confidence * 100)}% sure
            </span>
          ) : null}
        </button>
      </div>
      <p
        className="text"
        // The "corrected" marker is drawn by CSS, not added as a node, so the
        // line's text — what a reader copies and what the export holds — is
        // only what was said.
        title={corrected ? 'Corrected after the meeting, with the whole recording heard' : undefined}
      >
        {segment.text}
        {!segment.isFinal ? <span className="caret" aria-hidden="true" /> : null}
      </p>
    </li>
  );
}
